import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  IconButton,
  Divider,
  Link,
} from '@mui/material';
import {
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
  YouTube,
  Phone,
  Email,
  LocationOn,
} from '@mui/icons-material';
import { scrollToSection } from '../../utils/scroll';
import { CONTACT_CALL_DISPLAY, CONTACT_EMAIL } from '../../utils/contact';

// Map of footer link labels to section IDs for scroll navigation
const linkToSection = {
  'About Us': 'about',
  'Our Team': 'about',
  'Success Stories': 'portfolio',
  'Testimonials': 'home',
  'FAQs': 'home',
  'Contact': 'contact',
  'MBBS in India': 'mbbs',
  'MBBS in Bangladesh': 'portfolio',
  'MBBS in Nepal': 'portfolio',
  'MBBS in Russia': 'portfolio',
  'MD/MS Admissions': 'mbbs',
  'NEET Counselling': 'mbbs',
  'MCI/NMC': 'contact',
  'NTA NEET': 'contact',
  'MCC': 'contact',
  'FMGE': 'contact',
  'NBE': 'contact',
  'DCI': 'contact',
};

const Footer = () => {
  return (
    <Box component="footer">
      {/* Main Footer */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 6 }}>
        <Container maxWidth="xl">
          <Grid container spacing={4}>
            {/* Column 1: Logo & Info */}
            <Grid item xs={12} md={3}>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ mb: 1 }}>
                  <Box
                    component="img"
                    src="/LearnRabbit.png"
                    alt="Learn Rabbit Logo"
                    sx={{ height: 55, width: 'auto', borderRadius: '8px', objectFit: 'contain' }}
                  />
                </Box>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Your trusted partner for medical education in India and abroad. 
                  We provide comprehensive guidance for MBBS, MD, and MS admissions.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <IconButton size="small" sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }} onClick={() => scrollToSection('contact')}>
                  <Facebook />
                </IconButton>
                <IconButton size="small" sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }} onClick={() => scrollToSection('contact')}>
                  <Twitter />
                </IconButton>
                <IconButton size="small" sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }} onClick={() => scrollToSection('contact')}>
                  <Instagram />
                </IconButton>
                <IconButton size="small" sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }} onClick={() => scrollToSection('contact')}>
                  <LinkedIn />
                </IconButton>
                <IconButton size="small" sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }} onClick={() => scrollToSection('contact')}>
                  <YouTube />
                </IconButton>
              </Box>
            </Grid>

            {/* Column 2: Quick Links */}
            <Grid item xs={12} sm={4} md={2}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'white' }}>
                Quick Links
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {['About Us', 'Our Team', 'Success Stories', 'Testimonials', 'FAQs', 'Contact'].map(
                  (link) => (
                    <Link
                      key={link}
                      onClick={() => scrollToSection(linkToSection[link] || 'home')}
                      color="inherit"
                      underline="hover"
                      sx={{ opacity: 0.8, '&:hover': { opacity: 1 }, cursor: 'pointer' }}
                    >
                      {link}
                    </Link>
                  )
                )}
              </Box>
            </Grid>

            {/* Column 3: Admissions */}
            <Grid item xs={12} sm={4} md={3}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'white' }}>
                Admissions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[
                  'MBBS in India',
                  'MBBS in Bangladesh',
                  'MBBS in Nepal',
                  'MBBS in Russia',
                  'MD/MS Admissions',
                  'NEET Counselling',
                ].map((link) => (
                  <Link
                    key={link}
                    onClick={() => scrollToSection(linkToSection[link] || 'home')}
                    color="inherit"
                    underline="hover"
                    sx={{ opacity: 0.8, '&:hover': { opacity: 1 }, cursor: 'pointer' }}
                  >
                    {link}
                  </Link>
                ))}
              </Box>
            </Grid>

            {/* Column 4: Important Links */}
            <Grid item xs={12} sm={4} md={2}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'white' }}>
                Important Links
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {['MCI/NMC', 'NTA NEET', 'MCC', 'FMGE', 'NBE', 'DCI'].map((link) => (
                  <Link
                    key={link}
                    onClick={() => scrollToSection(linkToSection[link] || 'home')}
                    color="inherit"
                    underline="hover"
                    sx={{ opacity: 0.8, '&:hover': { opacity: 1 }, cursor: 'pointer' }}
                  >
                    {link}
                  </Link>
                ))}
              </Box>
            </Grid>

            {/* Column 5: Contact */}
            <Grid item xs={12} md={2}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'white' }}>
                Contact Us
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOn sx={{ fontSize: 20 }} />
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    123 Education Street, New Delhi - 110001
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Phone sx={{ fontSize: 20 }} />
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  {CONTACT_CALL_DISPLAY}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Email sx={{ fontSize: 20 }} />
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  {CONTACT_EMAIL}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Bottom Bars */}
      <Box sx={{ bgcolor: 'primary.main', py: 2 }}>
        <Container maxWidth="xl">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Phone sx={{ color: 'white' }} />
              <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }}>
                {CONTACT_CALL_DISPLAY}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Email sx={{ color: 'white' }} />
              <Typography variant="body1" sx={{ color: 'white' }}>
                {CONTACT_EMAIL}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: 'flex', gap: 2 }}>
              <IconButton size="small" sx={{ color: 'white' }} onClick={() => scrollToSection('contact')}>
                <Facebook />
              </IconButton>
              <IconButton size="small" sx={{ color: 'white' }} onClick={() => scrollToSection('contact')}>
                <Instagram />
              </IconButton>
              <IconButton size="small" sx={{ color: 'white' }} onClick={() => scrollToSection('contact')}>
                <Twitter />
              </IconButton>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Copyright Bar */}
      <Box sx={{ bgcolor: 'primary.dark', color: 'white', py: 1.5, textAlign: 'center' }}>
        <Container maxWidth="xl">
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            © {new Date().getFullYear()} Learn Rabbit Pvt. Ltd. All rights reserved. | 
            Privacy Policy | Terms of Service
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default Footer;